import React from 'react';
import {
  assignmentBreakdown, foundText, OverviewItem, statusLabel, statusSentence,
} from '../model';
import { minutesLabel, questionsLabel } from '../words';
import { Icon } from './icons';
import { StatusPill } from './StatusPill';
import { DeleteTestPanel } from './DeleteTestPanel';

interface Props {
  item: OverviewItem;
  menuOpen: boolean;
  /** Сколько выдач «Выдано» не дают убрать тест в архив (после ответа 409), иначе null. */
  blocked: number | null;
  /** Пока идёт действие над одним из тестов, кнопки меню отключены: двойное нажатие не создаёт вторую копию. */
  busy: boolean;
  onOpen: () => void;
  onAssign: () => void;
  onToggleMenu: () => void;
  onCopy: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onGoGiven: () => void;
  deleting: boolean;
  onDelete: () => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
}

/** Строка теста в списке (состав — по макету v6): состояние, смысл, размер, кнопки и меню «Ещё». */
export const TestCard: React.FC<Props> = ({
  item, menuOpen, blocked, busy, onOpen, onAssign, onToggleMenu, onCopy, onArchive, onRestore, onGoGiven,
  deleting, onDelete, onConfirmDelete, onCancelDelete,
}) => {
  const archived = item.status === 'archived';
  const breakdown = item.status === 'assigned' ? assignmentBreakdown(item) : '';
  return (
    <article className="tw-card tw-test">
      <div className="tw-test-head">
        <h2 className="tw-test-title">{item.title}</h2>
        <StatusPill status={item.status} label={statusLabel(item)} />
      </div>
      <p className="tw-help">{statusSentence(item)}</p>
      <p className="tw-meta">{`${questionsLabel(item.questions_count)} · ${minutesLabel(item.time_limit_minutes)}`}</p>
      {breakdown && <p className="tw-help">{breakdown}</p>}
      {item.matched_people.length > 0 && <p className="tw-found-line">{foundText(item)}</p>}
      {/* Слова на кнопках одинаковы у всех карточек, поэтому имя каждой кнопки начинается с видимого слова и называет тест. */}
      <div className="tw-row tw-btn-row">
        <button type="button" className="tw-btn tw-btn-primary" aria-label={`Открыть тест: ${item.title}`} onClick={onOpen}>
          Открыть
        </button>
        {!archived && (
          <button type="button" className="tw-btn" aria-label={`Дать тест сотрудникам: ${item.title}`} onClick={onAssign}>
            Дать тест сотрудникам
          </button>
        )}
        <button
          type="button"
          className="tw-btn"
          aria-label={`Ещё: действия с тестом «${item.title}»`}
          aria-expanded={menuOpen}
          disabled={busy}
          onClick={onToggleMenu}
        >
          Ещё
          <Icon name="down" />
        </button>
      </div>
      {menuOpen && (
        <div className="tw-menu">
          <div className="tw-menu-item">
            <button type="button" className="tw-btn" disabled={busy} onClick={onCopy}>
              <Icon name="copy" />
              Сделать копию
            </button>
            <span className="tw-help">Появится такой же тест в состоянии «Готовится». Сотрудники его не увидят.</span>
          </div>
          {!archived && (
            <div className="tw-menu-item">
              <button type="button" className="tw-btn" disabled={busy} onClick={onArchive}>
                <Icon name="archive" />
                Убрать в архив
              </button>
              <span className="tw-help">Сотрудники перестанут видеть тест. Результаты сохранятся. Тест можно вернуть.</span>
            </div>
          )}
          {archived && (
            <div className="tw-menu-item">
              <button type="button" className="tw-btn" disabled={busy} onClick={onRestore}>Вернуть из архива</button>
              <span className="tw-help">Тест выйдет из архива. Сотрудники не увидят его, пока вы снова не откроете доступ.</span>
            </div>
          )}
          {blocked !== null && (
            <div className="tw-warn">
              <Icon name="info" />
              <span>{`Сейчас тест доступен сотрудникам: ${blocked}. Сначала приостановите выдачи на вкладке «Кому выдан».`}</span>
              <button type="button" className="tw-btn tw-btn-small" onClick={onGoGiven}>Перейти к выдачам</button>
            </div>
          )}
          <div className="tw-menu-item">
            <button type="button" className="tw-btn tw-btn-danger-outline" disabled={busy} onClick={onDelete}>
              <Icon name="x" />
              Удалить тест
            </button>
            <span className="tw-help">Удалит тест, его вопросы, назначения и результаты навсегда.</span>
          </div>
          {deleting && <DeleteTestPanel test={item} busy={busy} onConfirm={onConfirmDelete} onCancel={onCancelDelete} />}
        </div>
      )}
    </article>
  );
};
